// BM-H596 (23 Sep 2026): one block model for the letter, the same on both
// sides. The backend (backend/hub/ccl_blocks.py) numbers the draft's
// textblocks for the AI and names the template-fixed parts; this file walks
// the TipTap document the same way so a suggestion lands in the exact block
// it was written for, or nowhere at all. There is no "end of the letter"
// fallback anywhere in here, by design.

const QUOTES = { "\u2018": "'", "\u2019": "'", "\u201c": '"', "\u201d": '"', "\u00a0": " " };
// A list number needs its full stop ("1.", "2.1", "2.1."), so a house
// number such as "1 High Street" is left alone. Same rule as the backend.
const LIST_NUMBER_RE = /^\s*(?:\d+\.(?:\d+\.?)*|[a-z]\)|\([a-z0-9]+\))\s+/i;

export function normalise(text) {
  let s = String(text || "");
  s = s.replace(/[\u2018\u2019\u201c\u201d\u00a0]/g, (ch) => QUOTES[ch]);
  s = s.replace(/\s+/g, " ").trim();
  return s.replace(LIST_NUMBER_RE, "");
}

function slug(text) {
  return normalise(text).toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
}

// Name the template-fixed blocks by rule, exactly as the backend does.
function nameBlocks(blocks) {
  let emailIdx = -1;
  let salutationIdx = -1;
  let reSeen = false;
  blocks.forEach((b, i) => {
    const t = b.text;
    if (/^our ref:/i.test(t)) b.anchorName = "our_ref";
    else if (/^your ref:/i.test(t)) b.anchorName = "your_ref";
    else if (/^date:/i.test(t)) b.anchorName = "date";
    else if (/^email:/i.test(t) && emailIdx < 0) { b.anchorName = "email_line"; emailIdx = i; }
    else if (/^dear /i.test(t) && salutationIdx < 0) { b.anchorName = "salutation"; salutationIdx = i; }
    else if (/^re:/i.test(t) && !reSeen) { b.anchorName = "re_line"; reSeen = true; }
    else if (/^yours (sincerely|faithfully)/i.test(t)) b.anchorName = "sign_off";
    else if (b.isHeading && t) b.anchorName = `heading:${slug(t)}`;
  });
  if (emailIdx >= 0 && salutationIdx > emailIdx) {
    for (let i = emailIdx + 1; i < salutationIdx; i += 1) {
      if (!blocks[i].anchorName && blocks[i].text) blocks[i].anchorName = "address_block";
    }
  }
  return blocks;
}

// Ordered textblocks of a ProseMirror doc: { blockId, text, pos, node, anchorName }.
// Empty blocks keep their number, the same as the backend.
export function blocksFromDoc(doc) {
  const blocks = [];
  if (!doc) return blocks;
  doc.descendants((node, pos) => {
    if (!node.isTextblock) return true;
    blocks.push({
      blockId: blocks.length,
      text: normalise(node.textContent),
      pos,
      node,
      isHeading: node.type?.name === "heading",
      anchorName: null,
    });
    return false;
  });
  return nameBlocks(blocks);
}

function parseBlockId(value) {
  if (value === null || value === undefined || value === "") return null;
  const m = String(value).match(/^B?(\d+)$/i);
  return m ? Number(m[1]) : null;
}

// Find the block a suggestion was written for. By anchor name first (the Re
// line, the salutation, a heading), then by block number, and in both cases
// the block's text must still be what the check saw when block_text is known.
export function findSuggestionBlock(blocks, suggestion) {
  const expected = suggestion.blockText ? normalise(suggestion.blockText) : null;
  const same = (b) => !expected || b.text === expected;
  const name = suggestion.anchorName;
  if (name && name !== "address_block") {
    const named = blocks.filter((b) => b.anchorName === name);
    if (named.length >= 1) return same(named[0]) ? named[0] : null;
  }
  const idx = parseBlockId(suggestion.blockId);
  if (idx !== null && blocks[idx] && same(blocks[idx])) return blocks[idx];
  if (expected) {
    const matches = blocks.filter((b) => b.text === expected);
    if (matches.length === 1) return matches[0];
  }
  return null;
}

// Document positions of `target` inside one textblock node, walking inline
// children so formatting marks and hard breaks do not throw the offsets out.
export function rangeInBlock(block, target) {
  if (!block || !target) return null;
  const chars = [];
  block.node.forEach((child, offset) => {
    if (child.isText) {
      for (let i = 0; i < child.text.length; i += 1) {
        chars.push({ ch: child.text[i], pos: block.pos + 1 + offset + i });
      }
    } else {
      chars.push({ ch: " ", pos: block.pos + 1 + offset });
    }
  });
  const raw = chars.map((c) => QUOTES[c.ch] || c.ch).join("");
  const wanted = String(target).replace(/[\u2018\u2019\u201c\u201d\u00a0]/g, (ch) => QUOTES[ch]);
  let at = raw.indexOf(wanted);
  if (at < 0) at = raw.toLowerCase().indexOf(wanted.toLowerCase());
  if (at < 0 || raw.indexOf(wanted, at + 1) > -1) return null;
  const from = chars[at].pos;
  const to = chars[at + wanted.length - 1].pos + 1;
  return { from, to };
}

export const STALE_MESSAGE = "This part of the letter has changed since the check. Rerun the once-over";

// Apply one suggestion. Returns { ok: true } or { ok: false, reason }. On any
// failure the document is left exactly as it was.
//   replace      -> the target words are replaced in place, in that block
//   insert_after -> a new paragraph straight after that block
//   advisory     -> never applied
export function applySuggestion(editor, suggestion, toHtml = (t) => t) {
  if (!editor) return { ok: false, reason: "no_editor" };
  const op = suggestion.op || "advisory";
  if (op === "advisory") return { ok: false, reason: "advisory" };
  if (op === "remove") return removeBlocks(editor, suggestion);
  const text = suggestion.text ?? suggestion.replacement ?? "";
  if (!text) return { ok: false, reason: "no_text" };
  const blocks = blocksFromDoc(editor.state.doc);
  const block = findSuggestionBlock(blocks, suggestion);
  if (!block) return { ok: false, reason: "stale" };
  if (op === "replace") {
    const range = rangeInBlock(block, suggestion.targetText);
    if (!range) return { ok: false, reason: "stale" };
    editor.chain().focus().insertContentAt(range, { type: "text", text: text.trim() }).run();
    return { ok: true, op, blockId: block.blockId };
  }
  if (op === "insert_after") {
    const after = block.pos + block.node.nodeSize;
    editor.chain().focus().insertContentAt(after, toHtml(text)).run();
    return { ok: true, op, blockId: block.blockId };
  }
  return { ok: false, reason: "unknown_op" };
}

// BM-H3747: take out a run of whole paragraphs, blockId to endBlockId, in one
// step. Both ends must still read as they did when the AI checked the letter.
export function removeBlocks(editor, suggestion) {
  const blocks = blocksFromDoc(editor.state.doc);
  const start = findSuggestionBlock(blocks, suggestion);
  const end = findSuggestionBlock(blocks, {
    blockId: suggestion.endBlockId ?? suggestion.blockId,
    blockText: suggestion.endBlockText ?? suggestion.blockText,
  });
  if (!start || !end || end.blockId < start.blockId) return { ok: false, reason: "stale" };
  const from = start.pos;
  const to = end.pos + end.node.nodeSize;
  editor.chain().focus().deleteRange({ from, to }).run();
  return { ok: true, op: "remove", blockId: start.blockId };
}

// Undo one automatic pre-draft change: put `before` back where `after` now
// sits. Only when `after` is found in exactly one block.
export function undoChange(editor, change) {
  if (!editor || !change?.after) return { ok: false, reason: "no_change" };
  const blocks = blocksFromDoc(editor.state.doc);
  const hits = blocks.filter((b) => b.text && b.text.includes(normalise(change.after)));
  if (hits.length !== 1) return { ok: false, reason: "stale" };
  const range = rangeInBlock(hits[0], change.after);
  if (!range) return { ok: false, reason: "stale" };
  editor.chain().focus().insertContentAt(range, { type: "text", text: change.before }).run();
  return { ok: true };
}
